import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import {
  type Appearance,
  appearanceMaterial,
  type LegacyFinish,
  pieceAppearance,
} from "./appearance.ts";
import { request } from "./store.ts";
import type { TimelapseFrame } from "./timelapse.ts";
import { mountShelf, shelfView, type TagPlace } from "./ui/shelf.tsx";

/** A delivered piece kept on the shelf, as it was when delivered. */
export interface Piece {
  /** Set by the store. */
  id?: number;
  date: number;
  /** The customer's letter, copied: later versions of the game may word it differently. */
  request: {
    title: string;
    client: string;
    letter: string;
    wish: string;
    item: string;
    exhibit: string;
  };
  rank: string;
  total: number;
  /** The finished piece (rest positions and triangles). */
  positions: Float32Array;
  index: Uint32Array;
  /** The commemorative photo: the stamped slip with the score card. */
  photo: Blob;
  /** The kneading step by step (see Timelapse), its last frame the finished shape. */
  timelapse?: TimelapseFrame[];
  /** Full material, fixed color, and finish; absent on pieces stored by older versions. */
  appearance?: Appearance;
  /** Finish key stored by older versions, retained so existing saved pieces still render. */
  finish?: LegacyFinish;
  /** Kneaded freely: no customer (its request copy only names it), nothing judged. */
  free?: boolean;
}

// ---------- Store (IndexedDB) ----------

export const keepPiece = (piece: Piece) => request("shelf", "readwrite", (s) => s.add(piece));
export const loadPieces = () => request<Piece[]>("shelf", "readonly", (s) => s.getAll());
export const removePiece = (id: number) => request("shelf", "readwrite", (s) => s.delete(id));
export const clearPieces = () => request("shelf", "readwrite", (s) => s.clear());

// ---------- Scene ----------

const COLUMNS = 3;
/** Size of one compartment of the shelf (width, height, depth). */
const CELL = { w: 2.3, h: 2.5, d: 2 };
/** Every piece is scaled so its largest extent is this: models of about one size. */
const PIECE_SIZE = 1.3;
const PEDESTAL = { radius: 0.72, height: 0.22 };
/** Compartment rows in view at once; more scroll. */
const ROWS_IN_VIEW = 2;

/** Pedestal finish by rank: wood, bronze, silver or gold. */
export function pedestalMaterial(rank: string): THREE.Material {
  if (rank === "S" || rank === "SS") {
    return new THREE.MeshStandardMaterial({ color: 0xe8b84a, metalness: 0.9, roughness: 0.25 });
  }
  if (rank === "A") {
    return new THREE.MeshStandardMaterial({ color: 0xd4d8de, metalness: 0.9, roughness: 0.22 });
  }
  if (rank === "B") {
    return new THREE.MeshStandardMaterial({ color: 0xc27a45, metalness: 0.85, roughness: 0.32 });
  }
  return new THREE.MeshStandardMaterial({ color: 0xb07a4c, metalness: 0, roughness: 0.75 });
}

export const pieceMaterial = (piece: Piece) =>
  appearanceMaterial(pieceAppearance(piece.appearance, piece.finish));

const WOOD = new THREE.MeshStandardMaterial({ color: 0xc89868, roughness: 0.7 });
const WOOD_DARK = new THREE.MeshStandardMaterial({ color: 0x9c6c46, roughness: 0.75 });

/** The clay before firing, for the timelapse. */
const CLAY = new THREE.MeshPhysicalMaterial({
  color: 0xf2b48c,
  roughness: 0.55,
  sheen: 0.8,
  sheenRoughness: 0.45,
  sheenColor: new THREE.Color(0xffd6bd),
  clearcoat: 0.2,
  clearcoatRoughness: 0.5,
});
/** Size of a piece picked up to look at (its largest extent). */
const INSPECT_SIZE = 2.2;

/**
 * A piece picked up off the shelf: alone, turned freely with the pointer, and its kneading
 * scrubbed back through with a slider (the clay unfired until the last step).
 */
class Inspector {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
  private readonly controls: OrbitControls;
  private readonly mesh = new THREE.Mesh(new THREE.BufferGeometry(), CLAY);
  private piece: Piece | null = null;

  constructor(canvas: HTMLCanvasElement, environment: THREE.Texture) {
    this.scene.environment = environment;
    this.scene.environmentIntensity = 0.8;
    this.scene.background = new THREE.Color(0xf6e8d6);
    const key = new THREE.DirectionalLight(0xfff4e8, 1.4);
    key.position.set(2, 3, 1);
    this.camera.add(key);
    this.scene.add(this.camera, this.mesh);
    this.controls = new OrbitControls(this.camera, canvas);
    Object.assign(this.controls, {
      enabled: false,
      enablePan: false,
      enableDamping: true,
      minDistance: 3,
      maxDistance: 12,
    });
  }

  get isOpen(): boolean {
    return this.piece !== null;
  }

  open(piece: Piece): void {
    this.piece = piece;
    // Every frame at the finished piece's scale, about the clay's centre (kept at the origin).
    const final = new THREE.Box3().setFromArray(piece.positions);
    const size = final.getSize(new THREE.Vector3());
    this.mesh.scale.setScalar(INSPECT_SIZE / Math.max(size.x, size.y, size.z));
    const last = Math.max(0, (piece.timelapse?.length ?? 1) - 1);
    shelfView.lastFrame.value = last;
    this.camera.position.set(0, 1.4, 7);
    this.controls.target.set(0, 0, 0);
    this.controls.enabled = true;
    shelfView.inspecting.value = true;
    this.show(last);
  }

  close(): void {
    this.piece = null;
    this.controls.enabled = false;
    shelfView.inspecting.value = false;
    this.mesh.geometry.dispose();
    this.mesh.geometry = new THREE.BufferGeometry();
  }

  /** Step k of the kneading; the last is the piece in its saved appearance, at full detail. */
  show(k: number): void {
    const piece = this.piece!, frames = piece.timelapse ?? [];
    const last = k >= frames.length - 1;
    const geometry = new THREE.BufferGeometry();
    const { positions, index } = last ? piece : frames[k];
    geometry.setIndex(new THREE.BufferAttribute(index, 1));
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    this.mesh.geometry.dispose();
    this.mesh.geometry = geometry;
    this.mesh.material = last ? pieceMaterial(piece) : CLAY;
    shelfView.frame.value = k;
    shelfView.frameStrokes.value = frames.length
      ? frames[Math.min(k, frames.length - 1)].strokes
      : null;
  }

  update(dt: number, aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.controls.update(dt);
  }
}

interface Slot {
  piece: Piece;
  mesh: THREE.Mesh;
  pedestal: THREE.Mesh;
  /** Centre of the pedestal's top. */
  base: THREE.Vector3;
}

// ---------- As a 3D model ----------

/** Metres to one unit of clay: the lump to start from (2 across) is about a hand's width. */
const METRES = 0.05;
/** Room left between the pieces in a row (m). */
const GAP = 0.03;

/**
 * Pieces in a row on their pedestals, in the order given, as one GLB file. Each piece is a
 * node of its own, named for its request, with what it was delivered as in its extras.
 */
export async function piecesModel(pieces: Piece[]): Promise<Blob> {
  const scene = new THREE.Scene();
  let x = 0;
  pieces.forEach((piece, k) => {
    const g = new THREE.BufferGeometry();
    g.setIndex(new THREE.BufferAttribute(piece.index, 1));
    g.setAttribute("position", new THREE.BufferAttribute(piece.positions.slice(), 3));
    g.computeBoundingBox();
    const box = g.boundingBox!, size = box.getSize(new THREE.Vector3()).multiplyScalar(METRES);
    const c = box.getCenter(new THREE.Vector3());
    // Stood on the pedestal (in the shelf's proportions), centred over it.
    g.translate(-c.x, -box.min.y, -c.z);
    g.scale(METRES, METRES, METRES);
    g.computeVertexNormals();
    const largest = Math.max(size.x, size.y, size.z);
    const radius = 0.55 * largest, height = 0.17 * largest;

    const model = new THREE.Group();
    model.name = pieces.length > 1 ? `${k + 1}. ${piece.request.title}` : piece.request.title;
    model.userData = {
      date: new Date(piece.date).toISOString(),
      appearance: pieceAppearance(piece.appearance, piece.finish),
      ...(piece.free ? { free: true } : { rank: piece.rank, total: piece.total }),
    };
    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius * 1.06, height, 48),
      pedestalMaterial(piece.rank),
    );
    pedestal.name = "台座";
    pedestal.position.y = height / 2;
    const mesh = new THREE.Mesh(g, pieceMaterial(piece));
    mesh.name = piece.request.item;
    mesh.position.y = height;
    model.add(pedestal, mesh);

    const half = Math.max(radius * 1.06, size.x / 2);
    model.position.x = x + half;
    x += 2 * half + GAP;
    scene.add(model);
  });
  // The row centred on the origin.
  for (const model of scene.children) model.position.x -= (x - GAP) / 2;

  const glb = await new GLTFExporter().parseAsync(scene, { binary: true }) as ArrayBuffer;
  scene.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.geometry.dispose();
    if (o.name === "台座") o.material.dispose();
  });
  return new Blob([glb], { type: "model/gltf-binary" });
}

/**
 * The shop's display shelf of kept pieces, drawn in its own scene: compartments of three,
 * each piece on a pedestal with a small label. Clicking a piece brings it close, turning,
 * with its letter and its commemorative photo beside it; from there it can be taken off.
 * What is shown over the 3D view is ui/shelf.tsx's, told through `shelfView`.
 */
export class Shelf {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
  private readonly contents = new THREE.Group();
  private slots: Slot[] = [];
  private selected: Slot | null = null;
  private rows = 1;
  /** Scroll position in rows (0 = top row in view). */
  private scroll = 0;
  private onClose: () => void = () => {};
  private readonly lookAt = new THREE.Vector3();
  private readonly inspector: Inspector;
  private dragY: number | null = null;
  private dragged = 0;
  /** Keeps the chosen piece still instead of turning it (see ui/settings.tsx). */
  still = false;

  constructor(private readonly canvas: HTMLCanvasElement, environment: THREE.Texture) {
    this.scene.environment = environment;
    this.scene.environmentIntensity = 0.8;
    const key = new THREE.DirectionalLight(0xfff4e8, 1.2);
    key.position.set(2, 4, 5);
    this.scene.add(key, this.contents);
    this.scene.background = new THREE.Color(0xf6e8d6);

    this.inspector = new Inspector(canvas, environment);
    mountShelf(document.querySelector("#shelf")!, {
      close: () => this.close(),
      deselect: () => this.select(null),
      pickUp: () => {
        if (this.selected) this.inspector.open(this.selected.piece);
      },
      putDown: () => this.inspector.close(),
      showFrame: (k) => this.inspector.show(k),
      remove: () => this.remove(),
      modelOf: (piece) => piecesModel([piece]),
    });
    // The shelf's own pointer handling (scrolling, picking) rests while a piece is picked up.
    const idle = () => !this.isOpen || this.inspector.isOpen;
    canvas.addEventListener("pointerdown", (e) => {
      if (idle()) return;
      this.dragY = e.clientY;
      this.dragged = 0;
    });
    canvas.addEventListener("pointermove", (e) => {
      if (idle() || this.dragY === null) return;
      const dy = e.clientY - this.dragY;
      this.dragY = e.clientY;
      // A drag is no click, even with a piece chosen (where it does not scroll).
      this.dragged += Math.abs(dy);
      if (!this.selected) this.scrollBy(-dy / (canvas.clientHeight / ROWS_IN_VIEW));
    });
    canvas.addEventListener("pointerup", (e) => {
      if (idle()) return;
      this.dragY = null;
      if (this.dragged < 6) this.pick(e);
    });
    canvas.addEventListener("wheel", (e) => {
      if (!idle() && !this.selected) this.scrollBy(e.deltaY / 400);
    }, { passive: true });
  }

  get isOpen(): boolean {
    return shelfView.open.value;
  }

  /** Opens the shelf with whatever is kept; `onClose` runs when the player leaves it. */
  async open(onClose: () => void): Promise<void> {
    this.onClose = onClose;
    shelfView.pieces.value = null;
    shelfView.open.value = true;
    document.body.classList.add("shelf-open");
    let pieces: Piece[] = [];
    try {
      pieces = await loadPieces();
    } catch {
      // No storage (a private window, say): an empty shelf.
    }
    this.build(pieces.sort((a, b) => a.date - b.date));
  }

  /**
   * The way back, as the shelf's buttons go: the piece picked up put down, the one chosen put
   * back among the others, or the shelf left (null while it is not open).
   */
  get wayBack(): (() => void) | null {
    if (!this.isOpen) return null;
    if (this.inspector.isOpen) return () => this.inspector.close();
    if (this.selected) return () => this.select(null);
    return () => this.close();
  }

  close(): void {
    if (this.inspector.isOpen) this.inspector.close();
    this.select(null);
    shelfView.open.value = false;
    document.body.classList.remove("shelf-open");
    this.clear();
    this.onClose();
  }

  private clear(): void {
    for (const s of this.slots) {
      s.mesh.geometry.dispose();
      (s.pedestal.material as THREE.Material).dispose();
    }
    this.contents.clear();
    shelfView.tags.value = [];
    this.slots = [];
  }

  private build(pieces: Piece[]): void {
    this.clear();
    shelfView.pieces.value = pieces;
    this.rows = Math.max(1, Math.ceil(pieces.length / COLUMNS));
    this.scroll = 0;
    this.buildCabinet();
    pieces.forEach((piece, k) => {
      const row = Math.floor(k / COLUMNS), col = k % COLUMNS;
      const floor = -row * CELL.h;
      const x = (col - (COLUMNS - 1) / 2) * CELL.w;
      const pedestal = new THREE.Mesh(
        new THREE.CylinderGeometry(PEDESTAL.radius, PEDESTAL.radius * 1.08, PEDESTAL.height, 48),
        pedestalMaterial(piece.rank),
      );
      pedestal.position.set(x, floor + PEDESTAL.height / 2, 0);
      const base = new THREE.Vector3(x, floor + PEDESTAL.height, 0);
      const mesh = new THREE.Mesh(this.geometryOf(piece), pieceMaterial(piece));
      mesh.position.copy(base);
      this.contents.add(pedestal, mesh);
      this.slots.push({ piece, mesh, pedestal, base });
    });
  }

  /** The piece's mesh, scaled to PIECE_SIZE and standing on the origin. */
  private geometryOf(piece: Piece): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setIndex(new THREE.BufferAttribute(piece.index, 1));
    g.setAttribute("position", new THREE.BufferAttribute(piece.positions.slice(), 3));
    g.computeBoundingBox();
    const box = g.boundingBox!, size = box.getSize(new THREE.Vector3());
    const s = PIECE_SIZE / Math.max(size.x, size.y, size.z);
    const c = box.getCenter(new THREE.Vector3());
    g.translate(-c.x, -box.min.y, -c.z);
    g.scale(s, s, s);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }

  private buildCabinet(): void {
    const width = COLUMNS * CELL.w, height = this.rows * CELL.h;
    const box = (w: number, h: number, d: number, x: number, y: number, z: number, m = WOOD) => {
      const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
      b.position.set(x, y, z);
      this.contents.add(b);
    };
    const top = CELL.h, bottom = top - height;
    // The back stops short of the sides' ends, so no face of it lies on theirs.
    box(width + 0.3, height + 0.26, 0.1, 0, (top + bottom) / 2, -CELL.d / 2, WOOD_DARK);
    for (let r = 0; r <= this.rows; r++) {
      box(width + 0.3, 0.14, CELL.d, 0, top - r * CELL.h - 0.07, 0);
    }
    for (const x of [-width / 2 - 0.1, width / 2 + 0.1]) {
      box(0.16, height + 0.3, CELL.d, x, (top + bottom) / 2, 0);
    }
  }

  private scrollBy(rows: number): void {
    this.scroll = Math.min(Math.max(0, this.rows - ROWS_IN_VIEW), Math.max(0, this.scroll + rows));
  }

  /**
   * Picks the piece whose compartment was clicked: by where the piece sits on screen rather
   * than by its surface, so a click through a hole still counts.
   */
  private pick(e: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const p = new THREE.Vector3(), q = new THREE.Vector3();
    let best: Slot | null = null, nearest = Infinity;
    for (const slot of this.slots) {
      p.copy(slot.base).setY(slot.base.y + PIECE_SIZE / 2).project(this.camera);
      q.copy(slot.base).setX(slot.base.x + CELL.w / 2).setY(slot.base.y + PIECE_SIZE / 2)
        .project(this.camera);
      const sx = ((p.x + 1) / 2) * rect.width, sy = ((1 - p.y) / 2) * rect.height;
      const reach = Math.abs(q.x - p.x) / 2 * rect.width;
      const d = Math.hypot(x - sx, y - sy);
      if (d < reach && d < nearest) {
        best = slot;
        nearest = d;
      }
    }
    if (best) this.select(best);
    // A piece chosen, a click on nothing puts it back among the others.
    else if (this.selected) this.select(null);
  }

  private select(slot: Slot | null): void {
    // Chosen again (a click on it or around it): it goes on turning as it was.
    if (slot === this.selected) return;
    if (this.selected) this.selected.mesh.rotation.y = 0;
    this.selected = slot;
    shelfView.selected.value = slot?.piece ?? null;
  }

  /**
   * Takes the chosen piece off the shelf for good; the rest close up the gap. False if it
   * could not be (no storage).
   */
  private async remove(): Promise<boolean> {
    const slot = this.selected;
    if (!slot) return false;
    try {
      await removePiece(slot.piece.id!);
    } catch {
      return false;
    }
    const scroll = this.scroll;
    this.select(null);
    this.build(this.slots.filter((s) => s !== slot).map((s) => s.piece));
    this.scrollBy(scroll);
    return true;
  }

  /** Eases the camera to the view wanted, turns the chosen piece, and places the labels. */
  update(dt: number): void {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    if (this.inspector.isOpen) {
      this.inspector.update(dt, w / h);
      return;
    }
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    const s = this.selected;
    const goal = new THREE.Vector3(), look = new THREE.Vector3();
    if (s) {
      if (!this.still) s.mesh.rotation.y += dt * 0.6;
      // Close up, the piece a little left of centre (the letter and photo are on the right).
      const shift = w >= h ? 0.9 : 0;
      look.copy(s.base).add(new THREE.Vector3(shift, PIECE_SIZE * 0.45 - (w >= h ? 0 : 0.6), 0));
      goal.copy(look).add(new THREE.Vector3(0, 0.8, 5.8));
    } else {
      const width = COLUMNS * CELL.w + 0.8;
      const vfov = THREE.MathUtils.degToRad(this.camera.fov);
      const fitWidth = (width / 2) / (Math.tan(vfov / 2) * this.camera.aspect);
      const fitHeight = (ROWS_IN_VIEW * CELL.h / 2) / Math.tan(vfov / 2);
      const dist = Math.max(fitWidth, Math.min(fitHeight, fitWidth * 1.6)) + CELL.d / 2;
      const y = CELL.h - (this.scroll + Math.min(ROWS_IN_VIEW, this.rows) / 2) * CELL.h;
      look.set(0, y, 0);
      goal.set(0, y + 0.5, dist);
    }
    const k = 1 - Math.exp(-dt * 6);
    this.camera.position.lerp(goal, k);
    this.lookAt.lerp(look, k);
    this.camera.lookAt(this.lookAt);
    this.camera.updateMatrixWorld();

    const p = new THREE.Vector3();
    const places: TagPlace[] = this.slots.map((slot) => {
      p.set(slot.base.x, slot.base.y - PEDESTAL.height - 0.05, CELL.d / 2).project(this.camera);
      const visible = !s && p.z < 1 && p.y > -1.1 && p.y < 1.1;
      return visible
        ? { x: Math.round(((p.x + 1) / 2) * w), y: Math.round(((1 - p.y) / 2) * h) }
        : null;
    });
    // The labels move only while the camera does.
    const was = shelfView.tags.value;
    const moved = places.length !== was.length ||
      places.some((at, k) => at?.x !== was[k]?.x || at?.y !== was[k]?.y);
    if (moved) shelfView.tags.value = places;
  }

  render(renderer: THREE.WebGLRenderer): void {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, w, h);
    if (this.inspector.isOpen) renderer.render(this.inspector.scene, this.inspector.camera);
    else renderer.render(this.scene, this.camera);
  }
}
